import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { X } from 'lucide-react';
import { modeId } from '../lib/profiles';
import { playLinks } from '../lib/license';
import SECTOR from '../lib/sectorTours.json';
import { SECTOR_TOOLS } from './TourGuide';
import { uiZoom } from '../lib/uiScale';

export const startDemo = () => window.dispatchEvent(new Event('giss-demo'));

const OPEN = {
  detect_water: ['fs-tool-corr', 'fs-tool-netq', 'fs-tool-gnss', 'fs-tool-track'],
  detect_gas: ['fs-tool-kks-plan', 'fs-tool-track', 'fs-tool-gnss', 'fs-tool-netq'],
  detect_cable: ['fs-tool-fault', 'fs-tool-gnss', 'fs-tool-track', 'fs-tool-xbear'],
  pipe: ['fs-tool-netq', 'fs-tool-stake', 'fs-tool-gnss', 'fs-tool-track'],
  garden: ['fs-tool-garden', 'fs-tool-facade', 'fs-tool-gnss', 'fs-tool-track'],
  building: ['fs-tool-facade', 'fs-tool-studio', 'fs-tool-gnss', 'fs-tool-track'],
  outdoor: ['fs-tool-track', 'fs-tool-xbear', 'fs-tool-proj', 'fs-tool-gnss'],
  all: ['fs-tool-corr', 'fs-tool-fault', 'fs-tool-garden', 'fs-tool-facade', 'fs-tool-gnss', 'fs-tool-track'],
};
const q = (id) => document.querySelector(`[data-testid="${id}"]`);
const tap = (id) => { const e = q(id); e?.click(); return !!e; };
const sleep = (ms, alive) => new Promise((res) => { const s = Date.now(); const iv = setInterval(() => { if (!alive() || Date.now() - s >= ms) { clearInterval(iv); res(); } }, 100); });
const closeAll = () => {
  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  document.querySelectorAll('[data-testid$="-close-btn"]').forEach((b) => { if (b.offsetParent && !b.closest('.demo-layer')) b.click(); });
};
const drawerOpen = () => !!document.querySelector('.fs-drawer.open');

// steps of one loop for the current sector: [kind, payload]
function plan(t, lang) {
  const m = modeId();
  const txt = (SECTOR[lang] || SECTOR.de)[m] || [];
  const steps = [['caption', [t.pcTag, ''], 'top']];
  steps.push(['fs']);
  (SECTOR_TOOLS[m] || []).forEach((id, i) => txt[i] && steps.push(['spot', id, txt[i]]));
  (OPEN[m] || OPEN.all).forEach((id) => steps.push(['open', id]));
  steps.push(['exitfs'], ['scroll', 'export-center', [t.pcF3.split(' – ')[0], t.pcF3.split(' – ')[1]]], ['qr']);
  return steps;
}

export default function DemoMode({ t, lang }) {
  const [on, setOn] = useState(false);
  const [cap, setCap] = useState(null);
  const [spot, setSpot] = useState(null);
  const [qr, setQr] = useState(null);
  const alive = useRef(false);
  useEffect(() => { const go = () => setOn(true); window.addEventListener('giss-demo', go); return () => window.removeEventListener('giss-demo', go); }, []);
  useEffect(() => {
    if (!on) return undefined;
    alive.current = true;
    let lock;
    navigator.wakeLock?.request('screen').then((l) => { lock = l; }).catch(() => {});
    QRCode.toDataURL(playLinks().open, { width: 420, margin: 1 }).then(setQr);
    const stop = (e) => { if (!e.isTrusted || e.target.closest?.('.demo-layer')) return; alive.current = false; setOn(false); };
    window.addEventListener('pointerdown', stop, true);
    const live = () => alive.current;
    (async () => {
      while (alive.current) {
        for (const [kind, a, b] of plan(t, lang)) {
          if (!alive.current) break;
          setSpot(null); setCap(null);
          if (kind === 'caption') { window.scrollTo({ top: 0, behavior: 'smooth' }); setCap({ t: a[0] }); await sleep(4500, live); }
          if (kind === 'fs') { if (!q('fs-exit-btn')) tap('fs-enter-btn'); await sleep(1500, live); tap('fs-tool-fit-all'); await sleep(2500, live); }
          if (kind === 'spot') {
            if (!drawerOpen()) { tap('fs-tools-tab'); await sleep(700, live); }
            if (!q(a)) continue;
            q(a).scrollIntoView({ block: 'center' }); await sleep(400, live);
            const r = q(a)?.getBoundingClientRect();
            const z = uiZoom(); if (r) setSpot({ top: r.top / z, left: r.left / z, width: r.width / z, height: r.height / z });
            setCap({ t: b[0], b: b[1] }); await sleep(4000, live);
          }
          if (kind === 'open') {
            if (!drawerOpen()) { tap('fs-tools-tab'); await sleep(700, live); }
            if (!q(a)) continue;
            const label = q(a)?.innerText?.split('\n')[0];
            tap(a); setCap({ t: label }); await sleep(6000, live); closeAll(); await sleep(900, live);
          }
          if (kind === 'exitfs') { closeAll(); if (drawerOpen()) tap('fs-tools-tab'); tap('fs-exit-btn'); await sleep(1200, live); }
          if (kind === 'scroll' && q(a)) { q(a).scrollIntoView({ block: 'center', behavior: 'smooth' }); setCap({ t: b[0], b: b[1] }); await sleep(5000, live); }
          if (kind === 'qr') { setCap({ qr: true }); await sleep(7000, live); }
        }
      }
    })();
    return () => {
      alive.current = false;
      window.removeEventListener('pointerdown', stop, true);
      lock?.release?.();
      setCap(null); setSpot(null);
      closeAll(); if (drawerOpen()) tap('fs-tools-tab'); tap('fs-exit-btn');
    };
  }, [on, t, lang]);
  if (!on) return null;
  return createPortal(
    <div className="demo-layer" data-testid="demo-layer">
      {spot && <div className="tour-spot" style={{ top: spot.top - 6, left: spot.left - 6, width: spot.width + 12, height: spot.height + 12 }} />}
      {cap && !cap.qr && (
        <div className="demo-cap" data-testid="demo-caption"><b>{cap.t}</b>{cap.b && <p>{cap.b}</p>}</div>
      )}
      {cap?.qr && qr && (
        <div className="demo-qr" data-testid="demo-qr"><img src={qr} alt="QR" /><div><b>GISS FieldRapport Pro</b><p className="demo-big">{t.playTrial7}</p><p>{t.pcScan}</p></div></div>
      )}
      <button className="demo-stop" data-testid="demo-stop-btn" onClick={() => { alive.current = false; setOn(false); }}><X size={16} /> {t.demoStop}</button>
    </div>,
    document.body,
  );
}
